import { cloneElement } from 'react';

import { Tooltip } from '@ingrid/luterkort-react';

import { ConditionalWrapper } from '~src/components/ConditionalWrapper/ConditionalWrapper';
import { useTranslation } from '~src/i18n/use-translation';
import { usePermissions } from '~src/main/hooks/usePermissions';
import { TransportRole } from '~src/main/providers/PermissionProvider';

interface Props {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  children: React.ReactElement<any>;
  roles: TransportRole[];
  withTooltip?: boolean;
}

/**
 * To be used around elements that accepts some kind of disabled state.
 * Checks the given role against the users role, and fowards down the disabled prop down in the tree.
 *
 * Optionally, it may also surround the element with a tooltip, explaining that the user lacks authorization for the given action.
 */
export function RoleGuard({ children, roles, withTooltip = false }: Props) {
  const { hasRole } = usePermissions();
  const { t } = useTranslation();

  const preventEdit = !hasRole(roles);
  const disabled = preventEdit ? preventEdit : undefined;

  const cloned = cloneElement(children, {
    'aria-disabled': disabled,
    'data-disabled': disabled,
    disabled,
  });

  return (
    <ConditionalWrapper
      wrapper={child => <Tooltip content={t('auth.forbidden')}>{child}</Tooltip>}
      when={withTooltip && preventEdit}
    >
      <div>{cloned}</div>
    </ConditionalWrapper>
  );
}
